<script setup>
import { Layout, ConfigProvider, Spin } from 'ant-design-vue'
import { useRoute, useRouter } from 'vue-router'
import { ref, watch, onMounted, onBeforeUnmount, reactive } from 'vue'
import BreadHeader from '@/components/bread-header'
import zhCN from 'ant-design-vue/es/locale/zh_CN'
import { ensureAuthenticated } from './service/axios'
import { getToken } from './service/auth'
import { isEmbedMode, listenDistributionToken, notifyDistributionReady } from '@/utils/embed'
import { loadSpringFestivalRanges } from '@/utils/ticketRefundRescheduleRules'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
dayjs.extend(duration)

const state = reactive()

const { Content } = Layout
const isLogin = ref(false)
const route = useRoute()
const router = useRouter()
const ready = ref(false)
const embed = isEmbedMode()
const authError = ref('')
const authLoading = ref(embed)
let stopListenToken = null

async function bootstrapAuth() {
  authLoading.value = true
  authError.value = ''
  const token = await ensureAuthenticated()
  authLoading.value = false
  if (token) {
    return true
  }
  authError.value = '未获取到分销登录信息，请刷新页面或重新从分销系统进入'
  return false
}

onMounted(async () => {
  if (embed) {
    stopListenToken = listenDistributionToken(async () => {
      if (getToken()) {
        return
      }
      authLoading.value = true
      authError.value = ''
      const token = await ensureAuthenticated()
      authLoading.value = false
      if (token) {
        ready.value = true
      }
    })
  }

  await Promise.all([bootstrapAuth(), loadSpringFestivalRanges()])
  ready.value = true
  if (embed) {
    notifyDistributionReady()
    if (stopListenToken) {
      stopListenToken()
      stopListenToken = null
    }
  }
  if (route.path === '/login') {
    router.replace('/')
  }
})

onBeforeUnmount(() => {
  if (stopListenToken) {
    stopListenToken()
  }
})

watch(
  () => route.path,
  (newRoute) => {
    isLogin.value = newRoute === '/login'
  }
)
</script>
<template>
  <ConfigProvider :locale="zhCN">
    <div v-if="embed" class="embed-root">
      <div v-if="authLoading" class="auth-loading">
        <Spin size="large" />
      </div>
      <div v-else-if="authError" class="auth-error">{{ authError }}</div>
      <template v-else-if="ready">
        <BreadHeader v-if="!isLogin" />
        <router-view v-slot="{ Component }">
          <keep-alive :include="['home', 'ticketSearch', 'passenger', 'ticketList']">
            <component :is="Component" />
          </keep-alive>
        </router-view>
      </template>
    </div>
    <Layout v-else>
      <Layout
        class="page-wrapper"
        :class="{ isLogin }"
      >
        <Content class="app-wrapper" :class="{ isLogin }">
          <ConfigProvider :locale="zhCN">
            <div v-if="authLoading" class="auth-loading">
              <Spin size="large" />
            </div>
            <div v-else-if="authError" class="auth-error">{{ authError }}</div>
            <BreadHeader v-if="!isLogin && ready && !authError && !authLoading" />
            <router-view v-if="ready && !authError && !authLoading" />
          </ConfigProvider>
        </Content>
      </Layout>
    </Layout>
  </ConfigProvider>
</template>

<style lang="scss" scoped>
.embed-root {
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  min-height: 0;
  padding: 12px 16px 16px;
  background: transparent;
  overflow-x: hidden;
  overflow-y: auto;
  contain: layout style;
}

.page-wrapper {
  box-sizing: border-box;
  min-height: 100vh;
  background: linear-gradient(180deg, #f5f8fe 0%, #eff3fb 100%);
  .app-wrapper {
    box-sizing: border-box;
    margin: 12px 16px 16px;
    padding: 16px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(2px);
    transition: all 0.2s;
    width: 100%;
  }
}

.page-wrapper.embed {
  min-height: auto;
  background: transparent;
}

.page-wrapper.embed .app-wrapper {
  margin: 0;
  padding: 8px;
  border-radius: 0;
  background: transparent;
  backdrop-filter: none;
}

.isLogin.page-wrapper {
  padding: 0;
}
.ant-layout-content {
  min-height: 100vh;
}
.page-wrapper.embed.ant-layout-content,
.page-wrapper.embed .ant-layout-content {
  min-height: auto;
}
.isLogin.ant-layout-content {
  height: 100vh;
  margin: 0;
  padding: 0;
}
.auth-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
}

.embed-root .auth-loading {
  min-height: 280px;
}

.embed-root .auth-error {
  margin: 0;
}
.auth-error {
  margin: 24px;
  padding: 16px 20px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
}
::v-deep {
  .ant-layout-content {
    transition: none;
  }
}
</style>
